import { Tabs, TabsContent, TabsList, TabsTrigger } from '@parcel/ui';
import * as React from 'react';
import type { RecordRef, RecordSeed } from '../../lib/record-detail.js';
import { type DetailMode, HeaderPartContext } from './entity-drawer-shell.js';

/** Chrome the detail host passes to every entity content component. */
export interface RecordDetailChromeProps {
  mode: DetailMode;
  onToggleMode?: () => void;
  backLabel?: string;
  onBack?: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  /** In-place navigation to a related record; rows are inert when absent. */
  onNavigate?: (ref: RecordRef, opts: { seed?: RecordSeed | null; fromLabel: string }) => void;
  /** The tab to open on; the first tab when absent or not shown. */
  initialTab?: string;
}

export interface EntityDetailTab {
  value: string;
  label: string;
  count?: number;
  /** Shown in place of the count, as when a full page reads as "200+". */
  countLabel?: string;
  content?: React.ReactNode;
  /** The tab only exists to hold the summary, so it disappears when the rail shows it. */
  drawerOnly?: boolean;
}

export function resolveDetailTabs(
  tabs: EntityDetailTab[],
  mode: DetailMode,
  requested: string,
): { visible: EntityDetailTab[]; active: string } {
  const visible = mode === 'expanded' ? tabs.filter((tab) => !tab.drawerOnly) : tabs;
  const active = visible.some((tab) => tab.value === requested)
    ? requested
    : (visible[0]?.value ?? requested);
  return { visible, active };
}

interface EntityDetailLayoutProps {
  mode: DetailMode;
  header: React.ReactNode;
  /** Cover content above the tabs (imagery, stage); it scrolls away with the page. */
  lead?: React.ReactNode;
  summary: React.ReactNode;
  tabs: EntityDetailTab[];
  summaryTab?: string;
  initialTab?: string;
}

export function EntityDetailLayout({
  mode,
  header,
  lead,
  summary,
  tabs,
  summaryTab = 'overview',
  initialTab,
}: EntityDetailLayoutProps) {
  const [requested, setRequested] = React.useState(initialTab ?? tabs[0]?.value ?? summaryTab);
  const { visible, active } = resolveDetailTabs(tabs, mode, requested);
  const expanded = mode === 'expanded';
  const summaryBlock = (
    <div data-detail-summary="" className="space-y-6 [&>*+*]:border-t [&>*+*]:pt-6">
      {summary}
    </div>
  );

  const main = (
    <div data-detail-main="" className="flex min-h-0 min-w-0 flex-1 flex-col">
      {expanded ? (
        <HeaderPartContext.Provider value="identity">{header}</HeaderPartContext.Provider>
      ) : null}
      <div data-detail-scroll="" className="min-h-0 flex-1 overflow-y-auto">
        {lead ? <div className="space-y-5 px-6 pt-1">{lead}</div> : null}
        <Tabs value={active} onValueChange={setRequested} className={lead ? 'mt-3' : undefined}>
          <div className="sticky top-0 z-10 border-b bg-background px-6">
            <TabsList className="h-11 gap-6 border-0">
              {visible.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value} className="h-11 px-0">
                  {tab.label}
                  {tab.count != null && tab.count > 0 ? (
                    <span className="ml-2 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-green-100 px-1.5 text-xs font-medium text-green-700 dark:bg-green-400/15 dark:text-green-300">
                      {tab.countLabel ?? tab.count}
                    </span>
                  ) : null}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          {visible.map((tab) => (
            // `isolate` keeps z-indexed content (timeline avatars, map overlays) under the sticky tabs.
            <TabsContent
              key={tab.value}
              value={tab.value}
              className="isolate mt-0 space-y-6 px-6 py-6"
            >
              {tab.content}
              {!expanded && tab.value === summaryTab ? summaryBlock : null}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );

  if (!expanded) {
    return (
      <>
        {header}
        <div className="flex min-h-0 flex-1 flex-col">{main}</div>
      </>
    );
  }

  // The bar spans the page; the identity stays pinned atop the main column and the rail runs up to the bar.
  return (
    <>
      <HeaderPartContext.Provider value="bar">{header}</HeaderPartContext.Provider>
      <div className="flex min-h-0 flex-1">
        {main}
        <aside
          data-detail-rail=""
          className="w-[22rem] shrink-0 overflow-y-auto border-l px-6 py-6"
        >
          {summaryBlock}
        </aside>
      </div>
    </>
  );
}
